// Import the mixins
import { BugsnagService } from '@/mixins/services/bugsnag/BugsnagService'
import { SpinnerMethods } from '@/mixins/helpers/SpinnerMethods'
import { GenerateUID } from '@/mixins/helpers/GenerateUID'

// Import the repository
import { UnitsRepository } from '@/repositories/units/UnitsRepository'

// Import firebase
import { propertiesModel, unitsModel, companiesModel } from '@/firebase/firebase-config'

export const CreateUnit = {

    /*
	**  Register the mixins
	*/

	mixins: [

        BugsnagService,
        SpinnerMethods,
        GenerateUID,
        UnitsRepository

    ],

    /*
	**  Set up the data
	*/

	data: () => ({

        processing: false,
        create_unit_error: false,
        create_unit_validate_complete: false,
        create_unit_set_props: false,
        create_unit_document: false,
        update_property_document: false,
        update_company_document: false

    }),

    /*
    **  Watch em'
    */

    watch: {

        create_unit_error: {

            immediate: true, // Load on created
            handler(create_unit_error) {

                /*
                **  Handle the error
                **  @method: @here
                */

                if (create_unit_error) this.handleCreateUnitError();

            }

        },

        /*
        **  Step 1
        **  @prop: @here
        */

        create_unit_validate_complete: {

            immediate: true, // Load on created
            handler(create_unit_validate_complete) {

                /*
                **  Check if payment methods have been set
                **  @method: @here
                */

                if (create_unit_validate_complete && !this.create_unit_error) this.setCreateUnitProps();

            }

        }, 

        /*
        **  Step 2
        **  @prop: @here
        */

        create_unit_set_props: {

            immediate: true, // Load on created
            handler(create_unit_set_props) {

                /*
                **  Create the unit document
                **  @method: @here
                */

                if (create_unit_set_props && !this.create_unit_error) this.createUnitDocument();

            }

        }, 

        /*
        **  Step 3
        **  @prop: @here
        */

        create_unit_document: {

            immediate: true, // Load on created
            handler(create_unit_document) {

                /*
                **  Update the property unit metrics
                **  @method: @here
                */

                if (create_unit_document && !this.create_unit_error) this.updatePropertyUnitMetrics();

            }

        }, 

        /*
        **  Step 4
        **  @prop: @here
        */

        update_property_document: {

            immediate: true, // Load on created
            handler(update_property_document) {

                /*
                **  Update the company metrics
                **  @method: @here
                */

                if (update_property_document && !this.create_unit_error) this.updateCompanyUnitMetrics();

            }

        }, 

        /*
        **  Step 5
        **  @prop: @here
        */

        update_company_document: {

            immediate: true, // Load on created
            handler(update_company_document) {

                /*
                **  Complete the event
                **  @method: @here
                */

                if (update_company_document && !this.create_unit_error) this.completeCreateUnitEvent();

            }

        }

    },

    /*
    **  Methods
    */

    methods: {

        createUnitHandler: function() {

            try {

                /*
                **  If processing, return
                **  @prop: @here
                */

                if (this.processing) return;

                /*
                **  Set the processing prop
                **  @prop: @here
                */

                this.processing = true;

                /*
                **  Show the spinner
                **  @method: Mixins/Helpers/SpinnerMethods
                */

                this.showSpinner();

                /*
                **  Validate
                **  @method: @here
                */

                this.validate();

            } catch (e) {

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
            
                this.reportAuthBugsnagError({
                    title: 'Unable to create the unit in the handler.',
                    errorType: 'Application',
                    section: 'CreateUnit',
                    uid: '4Gb8vWfN1Hr9CcQhkTif',
                    error: e
                });

            }

        },

        handleCreateUnitError: function() {

            try {

                /*
                **  Set the processing prop
                **  @prop: @here
                */

                this.processing = false;
    
                /*
                **  Hide the spinner
                **  @method: Mixins/Helpers/SpinnerMethods
                */

                this.hideSpinner();

                /*
                **  Throw error message
                **  @service: SweetAlert2
                */

                this.$swal('Uh Oh!', 'We ran into some problems while trying to create the unit.', 'error');

            } catch (e) {

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */

                this.reportAuthBugsnagError({
                    title: 'Unable to Handle an Error When Creating a Unit.',
                    errorType: 'Application',
                    section: 'Create Unit',
                    uid: 'qz9QcWyP68JemCVR7nzI',
                    error: e
                });

            }

        },

        validate: function() {

            try {

                /*
                **  Validate the required fields
                */

                if (this.new_unit.attributes.number == null || this.new_unit.attributes.number.length == 0 || this.new_unit.attributes.number == '') return false;
                if (this.new_unit.attributes.square_feet == 0 || this.new_unit.attributes.square_feet == null) return false;
                if (this.new_unit.attributes.bedrooms == 0) return false;
                if (this.new_unit.attributes.bathrooms == 0) return false;

                /*
                **  Set the completion prop
                **  @prop: @here
                */

                this.create_unit_validate_complete = true;

                /*
                **  Validation passed
                */

                return true;

            } catch (e) {

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
            
                this.reportAuthBugsnagError({
                    title: 'Validate the unit.',
                    errorType: 'Application',
                    section: 'CreateUnit',
                    uid: 'tY55SaD5fSfU0ZLTEYyZ',
                    error: e
                });

            }

        },

        setCreateUnitProps: function() {

            try {

                /*
                **  Convert the market rent prop to pennies
                **  @prop: @Repositories/Units/UnitsRepository
                */

                this.new_unit.attributes.rent.market = this.new_unit.attributes.rent.market * 100;

                /*
                **  Generate UID
                **  @method: @Mixins/Helpers/GenerateUID
                */

				this.new_unit.id = this.generateUID();

                /*
                **  Set the prop
                **  @prop: @here
                */

                this.create_unit_set_props = true;

            } catch (e) {

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
            
                this.reportAuthBugsnagError({
                    title: 'Set the create unit props.',
                    errorType: 'Application',
                    section: 'CreateUnit',
                    uid: 'czn02YSDFvBg9OVgovJ5',
                    error: e
                });

            }

        },

        createUnitDocument: function() {

            try {

                propertiesModel.doc(this.property.id).collection('units').doc(this.new_unit.id).set(this.new_unit)
                .then(() => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.create_unit_document = true;

                })
                .catch(error => {

                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */
                
                    this.reportAuthBugsnagError({
                        title: 'Create the unit document - Firestore Call.',
                        errorType: 'Application',
                        section: 'CreateUnit',
                        uid: 'rsmphHjykUwunVqCcGBv',
                        error: error
                    });

                });

            } catch (e) {

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
            
                this.reportAuthBugsnagError({
                    title: 'Create the unit document.',
                    errorType: 'Application',
                    section: 'CreateUnit',
                    uid: 'OBQsj8d1XXrjeAJpEzHS',
                    error: e
                });

            }

        },

        updatePropertyUnitMetrics: function() {

            try {

                propertiesModel.doc(this.$route.params.id).update({

                    'metrics.units.total': this.property.metrics.units.total + 1,
                    'metrics.units.total_market_rent': this.new_unit.attributes.rent.market,
                    'meta.updated_at': moment().unix()
    
                })
                .then(() => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.update_property_document = true;

                })
                .catch(error => {
    
                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Update Property Units Metrics in Firestore',
                        errorType: 'Firestore Database',
                        section: 'CreateUnit',
                        uid: 'fYcKQcDfgVMFZVlBl2wX',
                        error: error
                    });
    
                });

            } catch (e) {

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
            
                this.reportAuthBugsnagError({
                    title: 'Update the property document.',
                    errorType: 'Application',
                    section: 'CreateUnit',
                    uid: 'ycYHUKtueot37WijKABr',
                    error: e
                });

            }

        },

        updateCompanyUnitMetrics: function() {

            try {

                companiesModel.doc(this.company.id).update({

                    'metrics.totals.units': this.company.metrics.totals.units + 1,
				    'meta.updated_at': moment().unix()
    
                })
                .then(() => {

                    /*
                    **  Set the prop
                    **  @prop: @here
                    */

                    this.update_company_document = true;

                })
                .catch(error => {
    
                    /*
                    **  Send to Bugsnag
                    **  @service: Bugsnag/BugsnagService
                    */

                    this.reportAuthBugsnagError({
                        title: 'Update Company Units Metrics in Firestore',
                        errorType: 'Firestore Database',
                        section: 'CreateUnit',
                        uid: 'vI0725RjzHPIlnvJEeyr',
                        error: error
                    });
    
                });

            } catch (e) {

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
            
                this.reportAuthBugsnagError({
                    title: 'Update the company document.',
                    errorType: 'Application',
                    section: 'CreateUnit',
                    uid: 'JUatETnn597FyX6CZSww',
                    error: e
                });

            }

        },

        completeCreateUnitEvent: function() {

            try {

                /*
                **  Reset the new_unit prop
                **  @method: Repositories/Units/UnitsRepository
                */

				this.resetNewUnitProp();
 
                /*
                **  Reset the address prop
                **  @prop: Components/Property/Units/AddUnitMenu
                */

				this.show_address = false;

                /*
                **  Close the menu
                **  @method: Components/Property/Units/AddUnitMenu
                */

				this.closeMenu();

                /*
                **  Hide the spinner
                **  @method: Mixins/Helpers/SpinnerMethods
                */

				this.hideSpinner();
 
                /*
                **  Throw success message
                **  @service: @SweetAlert2
                */

	    		this.$swal('Success!', 'Your unit has been created.', 'success');

            } catch (e) {

                /*
                **  Send to Bugsnag
                **  @service: Bugsnag/BugsnagService
                */
            
                this.reportAuthBugsnagError({
                    title: 'Complete the create unit event.',
                    errorType: 'Application',
                    section: 'CreateUnit',
                    uid: 'nxBIs8k7bIGeIe1Bqvlr',
                    error: e
                });

            }

        }

    }

}